import { createApp, App as AppType } from 'vue'
import translation from '@/plugins/translation'
import useAppState, { Languages } from '@/composables/ui/ui'
import enTrans from '@/translations/en'
import skTrans from '@/translations/sk'
import useConfiguration, {
  ROOT_ELEMENT,
} from '@/composables/configuration/configuration'
import App from '@/App.vue'
import useEnvConfiguration from '@/composables/configuration/envConfiguration'
import useApiTimeout from '@/composables/apiTimeout'
import usePostsLoader from '@/composables/post/postLoader'
import usePostListMenu from '@/composables/ui/postListMenu'
import useEventBuffer from '@/composables/eventBuffer/eventBuffer'

let app: AppType | undefined = undefined
// let configString = ''

declare global {
  interface Window {
    anzu: Record<string, any> | undefined
  }
}

export default function useInitializer() {
  const {
    resolveWidgetConfiguration,
    resolveViewType,
    resolveLanguage,
    resolveExtSysConf,
    setVersion,
  } = useConfiguration()
  const { resolveEnvConfiguration } = useEnvConfiguration()
  const { language } = useAppState()
  const { configuration } = useConfiguration()
  const { initializeEventBuffer } = useEventBuffer()

  const destroyApp = () => {
    const { clearTimeouts } = useApiTimeout()
    const { clearListData } = usePostsLoader()
    const { reset } = usePostListMenu()

    if (typeof app !== 'undefined') {
      app.unmount()
      app = undefined
      clearListData()
      reset()
      clearTimeouts()
    }
  }

  const getHost = (): string | undefined => {
    if (window.anzu && window.anzu.forum && window.anzu.forum.forumHost) {
      const url = new URL(window.anzu.forum.forumHost)

      return url.host
    }
    return undefined
  }

  const getAppVersion = (): string => {
    if (window.anzu && window.anzu.forum && window.anzu.forum.version) {
      return window.anzu.forum.version
    }
    return ''
  }

  const initApp = (firstInit = false) => {
    resolveWidgetConfiguration()
    destroyApp()
    resolveExtSysConf()
    resolveViewType()
    setVersion(getAppVersion())
    resolveLanguage()
    resolveEnvConfiguration(getHost())
    initializeEventBuffer()

    if (firstInit && configuration.widget.widgetAutoloadDisabled) {
      return
    }

    app = createApp(App)
    app.use(translation, language.value === Languages.en ? enTrans : skTrans)
    app.mount(ROOT_ELEMENT)
  }

  const defineWindowFunction = () => {
    if (typeof window.anzu === 'undefined') {
      window.anzu = {}
    }
    if (typeof window.anzu.forum === 'undefined') {
      window.anzu.forum = {}
    }
    if (typeof window.anzu.forum.initializeWidget !== 'function') {
      window.anzu.forum.initializeWidget = initApp
    }
  }

  return {
    initApp,
    defineWindowFunction,
  }
}
